import React from 'react';
import type { PlanContext, PlanLesson } from '../../api/plan';
import { INTERFACE_TEXT } from '../../constants/interfaceText';
import { fill } from '../../today/todayViewModel';
import { useModalDialog } from '../turncard/PauseOverlay';
import styles from './Sheet.module.css';

interface SituationSheetProps {
  context: PlanContext;
  onClose: () => void;
  onStartLesson: (lessonId: string) => void;
  onStartCheckIn: (contextId: string) => void;
}

const Row: React.FC<{
  title: string;
  note?: string;
  action?: { label: string; primary?: boolean; run: () => void };
}> = ({ title, note, action }) => (
  <li className={styles.step}>
    <span>
      {title}
      {note && <small>{note}</small>}
    </span>
    {action && (
      <button
        type="button"
        className={action.primary ? styles.primary : undefined}
        onClick={action.run}
        aria-label={`${action.label} ${title.toLowerCase()}`}
      >
        {action.label}
      </button>
    )}
  </li>
);

const lessonRow = (
  context: PlanContext,
  lesson: PlanLesson,
  onStart: (lessonId: string) => void
): React.ReactElement => {
  const title = fill(INTERFACE_TEXT.tdLessonN, { n: lesson.order });
  if (context.state === 'locked' || lesson.state === 'locked') {
    return (
      <Row
        key={lesson.lesson_id}
        title={title}
        note={
          context.state === 'locked' || lesson.order <= 1
            ? undefined
            : fill(INTERFACE_TEXT.tdLessonAfter, { n: lesson.order - 1 })
        }
      />
    );
  }
  const done = lesson.state === 'completed';
  return (
    <Row
      key={lesson.lesson_id}
      title={title}
      note={done ? INTERFACE_TEXT.tdLessonDone : INTERFACE_TEXT.tdLessonNext}
      action={{
        label: done ? INTERFACE_TEXT.tdRedo : INTERFACE_TEXT.tdStart,
        primary: !done,
        run: () => onStart(lesson.lesson_id),
      }}
    />
  );
};

const checkInRow = (
  context: PlanContext,
  onStart: (contextId: string) => void
): React.ReactElement => {
  const title = INTERFACE_TEXT.tdCheckIn;
  if (context.state === 'locked' || !context.assessment_available) {
    return (
      <Row
        title={title}
        note={
          context.state === 'locked'
            ? undefined
            : INTERFACE_TEXT.tdCheckInAfterFirst
        }
      />
    );
  }
  const run = (): void => onStart(context.context_id);
  if (context.state === 'passed') {
    return (
      <Row
        title={title}
        note={INTERFACE_TEXT.tdCheckInPassed}
        action={{ label: INTERFACE_TEXT.tdRedo, run }}
      />
    );
  }
  const counts = context.assessment_counts_toward_pass;
  return (
    <Row
      title={title}
      note={
        counts ? INTERFACE_TEXT.tdCheckInReady : INTERFACE_TEXT.tdCheckInSoFar
      }
      action={{ label: INTERFACE_TEXT.tdStart, primary: counts, run }}
    />
  );
};

/** A situation's lessons and check-in, each with Start or Redo; a locked one says what opens it. */
export const SituationSheet: React.FC<SituationSheetProps> = ({
  context,
  onClose,
  onStartLesson,
  onStartCheckIn,
}) => {
  const ref = useModalDialog(onClose);
  const locked = context.state === 'locked';
  const names = context.missing_prerequisites
    .map((p) => p.context_name)
    .join(', ');
  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        ref={ref}
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={INTERFACE_TEXT.tdSheetClose}
        >
          ✕
        </button>
        <p className={styles.eyebrow}>
          {context.theme || INTERFACE_TEXT.tdThemeOther}
        </p>
        <h3 id="sheet-title" className={styles.heading}>
          {context.context_name}
        </h3>
        {context.blurb && <p className={styles.blurb}>{context.blurb}</p>}
        {locked && (
          <p className={styles.lockedNote}>
            {names
              ? fill(INTERFACE_TEXT.tdOpensWhenPass, { names })
              : INTERFACE_TEXT.tdLockedGeneric}
          </p>
        )}
        <ul className={styles.steps}>
          {context.lessons.map((lesson) =>
            lessonRow(context, lesson, onStartLesson)
          )}
          {checkInRow(context, onStartCheckIn)}
        </ul>
      </div>
    </div>
  );
};

export default SituationSheet;
